import React, { useMemo, useState } from 'react';
import { 
  ListTodo, 
  ChevronDown, 
  ChevronUp,
  Flame,
  AlertTriangle,
  Info,
  Lightbulb,
  FileText,
  Clock,
  TrendingUp,
  CheckCircle2,
  Filter,
  Search,
  SortAsc,
  X
} from 'lucide-react';
import { Issue, Severity, Category } from '../types';
import { t } from '../services/i18n';
import { IssueCard } from './IssueCard';

interface ActionPlanSectionProps {
  issues: Issue[];
  isExpanded: boolean;
  onToggle: () => void;
}

type SortOption = 'priority' | 'impact' | 'effort' | 'alphabetical';
type FilterOption = 'all' | 'critical' | 'high' | 'medium' | 'low' | 'info';

export const ActionPlanSection: React.FC<ActionPlanSectionProps> = ({
  issues,
  isExpanded,
  onToggle
}) => {
  const [sortBy, setSortBy] = useState<SortOption>('priority');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'detailed' | 'compact'>('detailed');
  const [showOnlyActionable, setShowOnlyActionable] = useState(false);

  // Group issues by priority
  const groupedIssues = useMemo(() => {
    const groups = {
      critical: issues.filter(i => i.severity === Severity.CRITICAL),
      high: issues.filter(i => i.severity === Severity.HIGH),
      medium: issues.filter(i => i.severity === Severity.MEDIUM),
      low: issues.filter(i => i.severity === Severity.LOW),
      info: issues.filter(i => i.severity === Severity.INFO)
    };
    return groups;
  }, [issues]);

  // Calculate quick wins (low effort, high impact)
  const quickWins = useMemo(() => {
    return issues.filter(issue => {
      const isLowEffort = (issue as any).timeToFix?.includes('min') || 
                          (issue as any).difficulty === 'Easy' ||
                          issue.severity === Severity.CRITICAL || 
                          issue.severity === Severity.HIGH;
      return isLowEffort && (issue.severity === Severity.CRITICAL || issue.severity === Severity.HIGH);
    }).slice(0, 5);
  }, [issues]);

  // Filter and sort issues
  const filteredAndSortedIssues = useMemo(() => {
    let filtered = [...issues];

    // Apply severity filter
    if (filterBy !== 'all') {
      const severityMap: Record<FilterOption, Severity> = {
        all: Severity.INFO,
        critical: Severity.CRITICAL,
        high: Severity.HIGH,
        medium: Severity.MEDIUM,
        low: Severity.LOW,
        info: Severity.INFO
      };
      filtered = filtered.filter(i => i.severity === severityMap[filterBy]);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(i => 
        i.title.toLowerCase().includes(query) ||
        i.description?.toLowerCase().includes(query) ||
        i.category?.toLowerCase().includes(query)
      );
    }

    // Apply actionable filter
    if (showOnlyActionable) {
      filtered = filtered.filter(i => 
        i.severity === Severity.CRITICAL || 
        i.severity === Severity.HIGH ||
        (i as any).autoFixAvailable === true
      );
    }

    // Sort
    switch (sortBy) {
      case 'priority':
        const priorityOrder = {
          [Severity.CRITICAL]: 0,
          [Severity.HIGH]: 1,
          [Severity.MEDIUM]: 2,
          [Severity.LOW]: 3,
          [Severity.INFO]: 4
        };
        return filtered.sort((a, b) => priorityOrder[a.severity] - priorityOrder[b.severity]);
      
      case 'alphabetical':
        return filtered.sort((a, b) => a.title.localeCompare(b.title));
      
      case 'impact':
        // Sort by severity (higher severity = higher impact)
        return filtered.sort((a, b) => {
          const impactOrder = {
            [Severity.CRITICAL]: 5,
            [Severity.HIGH]: 4,
            [Severity.MEDIUM]: 3,
            [Severity.LOW]: 2,
            [Severity.INFO]: 1
          };
          return impactOrder[b.severity] - impactOrder[a.severity];
        });
      
      case 'effort':
        // Sort by estimated time to fix (if available)
        return filtered.sort((a, b) => {
          const aTime = (a as any).timeToFix || '999';
          const bTime = (b as any).timeToFix || '999';
          return aTime.localeCompare(bTime);
        });
      
      default:
        return filtered;
    }
  }, [issues, filterBy, searchQuery, showOnlyActionable, sortBy]);

  // Calculate estimated total time
  const estimatedTime = useMemo(() => {
    const times = issues.map(i => {
      const timeStr = (i as any).timeToFix || '0 min';
      const match = timeStr.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    const total = times.reduce((sum, t) => sum + t, 0);
    if (total < 60) return `${total} min`;
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }, [issues]);

  const getSeverityIcon = (severity: Severity) => {
    switch (severity) {
      case Severity.CRITICAL: return <Flame className="w-4 h-4" />;
      case Severity.HIGH: return <AlertTriangle className="w-4 h-4" />;
      case Severity.MEDIUM: return <Info className="w-4 h-4" />;
      case Severity.LOW: return <Lightbulb className="w-4 h-4" />;
      case Severity.INFO: return <FileText className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity: Severity) => {
    switch (severity) {
      case Severity.CRITICAL: return 'text-red-400 bg-red-500/10 border-red-500/30';
      case Severity.HIGH: return 'text-orange-400 bg-orange-500/10 border-orange-500/30';
      case Severity.MEDIUM: return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      case Severity.LOW: return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      case Severity.INFO: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-6 shadow-sm transition-all hover:border-orange-500/50">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left group"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-500/20 rounded-lg group-hover:bg-orange-500/30 transition-colors">
            <ListTodo className="w-5 h-5 text-orange-400" />
          </div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-white">
              {t('results.actionPlan') || 'Action Plan'}
            </h2>
            {issues.length > 0 && (
              <span className="px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold">
                {issues.length}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
          ) : (
            <ChevronDown className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-6">
          {/* Quick Wins Section */}
          {quickWins.length > 0 && (
            <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-xl p-5 border border-emerald-500/30">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-bold text-white">
                  {t('results.quickWins') || 'Quick Wins'} ({quickWins.length})
                </h3>
              </div>
              <p className="text-sm text-slate-300 mb-4">
                {t('results.quickWinsDesc') || 'High-impact issues you can fix quickly to improve your SEO score immediately.'}
              </p>
              <div className="space-y-2">
                {quickWins.slice(0, 3).map((issue, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-3 border border-emerald-500/20">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getSeverityIcon(issue.severity)}
                          <span className="font-semibold text-white text-sm">{issue.title}</span>
                        </div>
                        <p className="text-xs text-slate-400 line-clamp-1">
                          {issue.description?.substring(0, 100)}...
                        </p>
                      </div>
                      {(issue as any).timeToFix && (
                        <span className="text-xs text-emerald-400 font-semibold whitespace-nowrap">
                          {(issue as any).timeToFix}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filter and Sort Bar */}
          <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
            <div className="flex flex-col md:flex-row gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder={t('results.searchIssues') || 'Search issues...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-slate-700 rounded"
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                )}
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {(['all', 'critical', 'high', 'medium', 'low'] as FilterOption[]).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setFilterBy(filter)}
                    className={`px-3 py-2 text-xs font-semibold rounded-lg whitespace-nowrap transition-all ${
                      filterBy === filter
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {filter === 'all' 
                      ? t('results.all') || 'All'
                      : t(`results.severity${filter.charAt(0).toUpperCase() + filter.slice(1)}`) || filter.charAt(0).toUpperCase() + filter.slice(1)
                    } ({groupedIssues[filter as keyof typeof groupedIssues]?.length || 0})
                  </button>
                ))}
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="appearance-none bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 pr-8 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="priority">{t('results.sortByPriority') || 'Sort by Priority'}</option>
                  <option value="impact">{t('results.sortByImpact') || 'Sort by Impact'}</option>
                  <option value="effort">{t('results.sortByEffort') || 'Sort by Effort'}</option>
                  <option value="alphabetical">{t('results.sortAlphabetical') || 'Alphabetical'}</option>
                </select>
                <SortAsc className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>

              {/* Toggle */}
              <label className="flex items-center gap-2 px-3 py-2 bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-600 transition-colors">
                <input
                  type="checkbox"
                  checked={showOnlyActionable}
                  onChange={(e) => setShowOnlyActionable(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs text-slate-300 whitespace-nowrap">
                  {t('results.actionableOnly') || 'Actionable only'}
                </span>
              </label>
            </div>

            {/* Stats Summary */}
            <div className="mt-4 pt-4 border-t border-slate-600 flex items-center justify-between text-xs">
              <div className="flex items-center gap-4">
                <span className="text-slate-400">
                  {t('results.showing') || 'Showing'}: <strong className="text-white">{filteredAndSortedIssues.length}</strong> {t('results.of') || 'of'} {issues.length}
                </span>
                <span className="text-slate-400">
                  {t('results.estimatedTime') || 'Est. time'}: <strong className="text-indigo-400">{estimatedTime}</strong>
                </span>
              </div>
              <button
                onClick={() => setViewMode(viewMode === 'detailed' ? 'compact' : 'detailed')}
                className="text-slate-400 hover:text-white transition-colors text-xs"
              >
                {viewMode === 'detailed' ? t('results.compactView') || 'Compact' : t('results.detailedView') || 'Detailed'}
              </button>
            </div>
          </div>

          {/* Priority Matrix */}
          <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              {t('results.priorityMatrix') || 'Priority Matrix'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <button
                onClick={() => setFilterBy('all')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                  filterBy === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {t('results.all') || 'All'} ({issues.length})
              </button>
              <button
                onClick={() => setFilterBy('critical')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg transition-all border flex items-center justify-center gap-1 ${
                  filterBy === 'critical'
                    ? 'bg-red-500/40 text-red-200 border-red-500/60'
                    : 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
                }`}
              >
                <Flame className="w-3 h-3" />
                {t('results.critical') || 'Critical'} ({groupedIssues.critical.length})
              </button>
              <button
                onClick={() => setFilterBy('high')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg transition-all border flex items-center justify-center gap-1 ${
                  filterBy === 'high'
                    ? 'bg-orange-500/40 text-orange-200 border-orange-500/60'
                    : 'bg-orange-500/20 text-orange-400 border-orange-500/30 hover:bg-orange-500/30'
                }`}
              >
                <AlertTriangle className="w-3 h-3" />
                {t('results.high') || 'High'} ({groupedIssues.high.length})
              </button>
              <button
                onClick={() => setFilterBy('medium')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg transition-all border flex items-center justify-center gap-1 ${
                  filterBy === 'medium'
                    ? 'bg-yellow-500/40 text-yellow-200 border-yellow-500/60'
                    : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/30'
                }`}
              >
                <Info className="w-3 h-3" />
                {t('results.medium') || 'Medium'} ({groupedIssues.medium.length})
              </button>
              <button
                onClick={() => setFilterBy('low')}
                className={`px-3 py-2 text-xs font-semibold rounded-lg transition-all border flex items-center justify-center gap-1 ${
                  filterBy === 'low'
                    ? 'bg-blue-500/40 text-blue-200 border-blue-500/60'
                    : 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30'
                }`}
              >
                <Lightbulb className="w-3 h-3" />
                {t('results.low') || 'Low'} ({groupedIssues.low.length})
              </button>
            </div>
          </div>

          {/* Issues List */}
          <div className="space-y-3">
            {filteredAndSortedIssues.length > 0 ? (
              filteredAndSortedIssues.map((issue, idx) => (
                <IssueCard key={`${issue.id}-${idx}`} issue={issue} index={idx} />
              ))
            ) : (
              <div className="text-center py-12 bg-slate-700/50 rounded-xl border border-slate-600">
                <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">
                  {t('results.noIssuesTitle') || 'Perfect Score!'}
                </h4>
                <p className="text-slate-300">
                  {searchQuery || filterBy !== 'all'
                    ? t('results.noIssuesFiltered') || 'No issues match your filters. Try adjusting your search or filters.'
                    : t('results.noIssuesMessage') || 'No issues found. Excellent work!'
                  }
                </p>
                {(searchQuery || filterBy !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilterBy('all');
                    }}
                    className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    {t('results.clearFilters') || 'Clear Filters'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

